'use strict'

// Avoid using 0 (zero) values in this file, as JavaScript reads 0 as falsey

import l, { isGs } from 'config/localization'
import { getLocalStorageValues } from 'utils/localStorageManager'

// assets
import { ReactComponent as PrivateIcon } from 'assets/images/svgs/icon-public-opp.svg'
import { ReactComponent as PublicIcon } from 'assets/images/svgs/icon-private-opp.svg'
import { ReactComponent as DonationIcon } from 'assets/images/cause-type-icons/money_icon.svg'
import { ReactComponent as GoodsIcon } from 'assets/images/cause-type-icons/goods_icon.svg'
import { ReactComponent as PledgeIcon } from 'assets/images/cause-type-icons/pledge_icon.svg'
import { ReactComponent as UserIcon } from 'assets/images/user.svg'
import { ReactComponent as VolunteerIcon } from 'assets/images/cause-type-icons/volunteer_icon.svg'

const subscriptionPrices = [
  {
    tier: 'SELF',
    pricing: {
      premium: { yearly: { m: 'R286', y: 'R3,436' }, monthly: 'R344' },
      enterprise: { yearly: { m: 'R3,392', y: 'R40,710' }, monthly: 'Available on a yearly plan' }
    }
  },
  {
    tier: 'NPO',
    pricing: {
      premium: { yearly: { m: 'R286', y: 'R3,436' }, monthly: 'R344' },
      enterprise: { yearly: { m: 'R3,392', y: 'R40,710' }, monthly: 'Available on a yearly plan' }
    }
  },
  {
    tier: 'REL',
    pricing: {
      premium: { yearly: { m: 'R286', y: 'R3,436' }, monthly: 'R344' },
      enterprise: { yearly: { m: 'R3,392', y: 'R40,710' }, monthly: 'Available on a yearly plan' }
    }
  },
  {
    tier: 'SCH',
    pricing: {
      premium: { yearly: { m: 'R631', y: 'R7,576' }, monthly: 'R746' },
      enterprise: { yearly: { m: 'R4,542', y: 'R54,510' }, monthly: 'Available on a yearly plan' }
    }
  },
  {
    tier: 'BUS',
    pricing: {
      premium: { yearly: { m: 'R1,091', y: 'R13,096' }, monthly: 'R1,436' },
      enterprise: { yearly: { m: 'R6,842', y: 'R82,110' }, monthly: 'Available on a yearly plan' }
    }
  }
]

const growsmartRoundLevels = [
  { id: 1, label: 'Level 1' },
  { id: 2, label: 'Level 2' },
  { id: 3, label: 'Quarter-final' },
  { id: 4, label: 'Semi-final' },
  { id: 5, label: 'Final' }
]

const getGoalTypes = () => {
  if (isGs) {
    return {
      GS_WORKSHOP: 'GsWorkshop',
      GS_ROUND: 'GsRound',
      GS_PROVINCIAL_EVENT: 'GsProvincialEvent'
    }
  }

  return {
    MONEY: 'Money',
    GOODS: 'Goods',
    VOLUNTEERING: 'Volunteering',
    PLEDGE: 'Pledge',
    UNAVAILABLE_DONATION_OPTIONS: 'N/A'
  }
}

const getGoalTypeNames = () => {
  if (isGs) {
    return { GsWorkshop: 'Workshop', GsRound: 'Round', GsProvincialEvent: 'Provincial Event' }
  }

  return { Money: 'Donations', Goods: 'Goods', Volunteering: 'Volunteers', Pledge: 'Pledges' }
}

const getGoalTypeCtas = () => {
  if (isGs) {
    return { GsWorkshop: 'RSVP', GsRound: 'Enrol learners', GsProvincialEvent: 'RSVP' }
  }

  return { Money: 'Donate', Goods: 'Donate', Volunteering: 'Volunteer', Pledge: 'Pledge' }
}

const getGoalTypeDeliverables = () => {
  if (isGs) {
    return { GsWorkshop: 'guests', GsRound: 'slots', GsProvincialEvent: 'guests' }
  }

  return {
    Money: 'rands',
    Goods: 'goods',
    Volunteering: 'volunteers',
    Pledge: 'pledges',
    Pledges: 'pledges' // Temporary until DB values are updated
  }
}

const goalTypes = getGoalTypes()
const goalTypeCtas = getGoalTypeCtas()
const goalTypeNames = getGoalTypeNames()
const goalTypeDeliverables = getGoalTypeDeliverables()

const getGoalTypesForForm = ({ full }) => {
  if (isGs) {
    return [
      { id: `${goalTypes.GS_WORKSHOP}`, name: goalTypeNames[goalTypes.GS_WORKSHOP] },
      { id: `${goalTypes.GS_ROUND}`, name: goalTypeNames[goalTypes.GS_ROUND] },
      { id: `${goalTypes.GS_PROVINCIAL_EVENT}`, name: goalTypeNames[goalTypes.GS_PROVINCIAL_EVENT] }
    ]
  }

  if (full) {
    return [
      {
        id: `${goalTypes.MONEY}`,
        name: goalTypeNames[goalTypes.MONEY],
        icon: DonationIcon,
        padding: '5px 3px 7px 2px'
      },
      {
        id: `${goalTypes.GOODS}`,
        name: goalTypeNames[goalTypes.GOODS],
        icon: GoodsIcon,
        padding: '5px 4px 6px 2px'
      },
      {
        id: `${goalTypes.VOLUNTEERING}`,
        name: goalTypeNames[goalTypes.VOLUNTEERING],
        icon: VolunteerIcon,
        padding: '3px 5px 6px 3px'
      },
      {
        id: `${goalTypes.PLEDGE}`,
        name: goalTypeNames[goalTypes.PLEDGE],
        icon: PledgeIcon,
        padding: '3px 5px 4px 2px'
      }
    ]
  }

  return [
    {
      id: `${goalTypes.VOLUNTEERING}`,
      name: goalTypeNames[goalTypes.VOLUNTEERING],
      icon: VolunteerIcon,
      padding: '3px 5px 6px 3px'
    },
    {
      id: `${goalTypes.PLEDGE}`,
      name: goalTypeNames[goalTypes.PLEDGE],
      icon: PledgeIcon,
      padding: '3px 5px 4px 2px'
    }
  ]
}

const goalTypesForForm = getGoalTypesForForm({ full: true })
const goalTypesForFormUnben = getGoalTypesForForm({ full: false })

const GS_LEARNER_GRADES = [
  { value: 4, label: '4' },
  { value: 5, label: '5' },
  { value: 6, label: '6' }
  // { value: 7, label: '7' },
  // { value: 8, label: '8' },
  // { value: 9, label: '9' },
  // { value: 10, label: '10' },
  // { value: 11, label: '11' },
  // { value: 12, label: '12' }
]

const GS_LEARNER_GENDERS = [
  { value: 1, label: 'Male' },
  { value: 2, label: 'Female' },
  { value: 3, label: 'Unspecified' }
]

const GS_RSVP_STATUS_NAMES = {
  ACTIVE: 'Applied',
  CANCELLED: 'Cancelled',
  ELIMINATED: 'GsEliminated',
  WINNER: 'GsWinner',
  ACTIVE: 'GsPendingRound',
  ABSENT: 'GsAbsent',
  WITHDREW: 'GsCancelled'
}

const GS_LEARNER_RACES = [
  { value: 1, label: 'African' },
  { value: 2, label: 'Coloured' },
  { value: 3, label: 'Indian' },
  { value: 4, label: 'White' },
  { value: 5, label: 'Other' }
]

const GS_PROV_OPTS = [
  { label: 'Eastern Cape', value: 'EC' },
  { label: 'Free State', value: 'FS' },
  { label: 'Gauteng', value: 'GP' },
  { label: 'Kwazulu Natal', value: 'KZN' },
  { label: 'Limpopo', value: 'LP' },
  { label: 'Mpumalanga', value: 'MP' },
  { label: 'North West', value: 'NW' },
  { label: 'Northern Cape', value: 'NC' },
  { label: 'Western Cape', value: 'WC' }
]

const SCHOLARSHIP_APPLICATION_STATUSES = {
  APPLIED: 'Applied',
  APPROVED: 'Approved',
  DENIED: 'Denied'
}

const GS_PROV_FULL_OPTS = [
  { label: 'Eastern Cape', value: 'Eastern Cape' },
  { label: 'Free State', value: 'Free State' },
  { label: 'Gauteng', value: 'Gauteng' },
  { label: 'Kwazulu Natal', value: 'Kwazulu Natal' },
  { label: 'Limpopo', value: 'Limpopo' },
  { label: 'Mpumalanga', value: 'Mpumalanga' },
  { label: 'North West', value: 'North West' },
  { label: 'Northern Cape', value: 'Northern Cape' },
  { label: 'Western Cape', value: 'Western Cape' }
]

const DEFAULT_GS_YEAR = '2025'

const GS_YEAR_OPTS = [
  { label: '2026', value: '2026' },
  { label: '2025', value: '2025' },
  { label: '2024', value: '2024' },
  { label: '2023', value: '2023' },
  { label: '2022', value: '2022' }
]

const GS_CURRENT_YEAR_OPTS = [
  { label: '2024', value: '2024' },
  { label: '2025', value: '2025' },
  { label: '2026', value: '2026' }
]

const BBBEE_LEVELS = [
  { value: 1, label: 'Level 1' },
  { value: 2, label: 'Level 2' },
  { value: 3, label: 'Level 3' },
  { value: 4, label: 'Level 4' }
]

const BENEFACTOR_TYPES = {
  USER: 'User',
  ORG: 'Organisation'
}

const BENEFICIARY_TYPES = {
  ANY: 'any',
  USER: 'User',
  NPO_ORG: 'Nonprofit',
  SCHOOL: 'School'
}

const CREATOR_CONTEXTS = {
  USER: 'User',
  ORG: 'Organisation'
}

const MAIL_DELIVERY_STATUSES = {
  SENT: 'Sent',
  DELIVERED: 'Delivered',
  OPENED: 'Opened',
  CLICKED: 'Clicked',
  FAILED: 'Failed'
}

const PLATFORM_EVENTS = {
  ACCOUNT_ACTIVATION_CONFIRMATION: 'Account Activation Confirmation',
  ACCOUNT_ACTIVATION_REQUEST: 'Account Activation Request',
  ADMIN_INVITE: 'Admin Opportunity Invitation',
  BENEFICIARY_SUPPORTER_SIGNUP_NOTIF: 'Beneficiary Supporter Signup Notification',
  COMMUNITY_BOOST_REQUEST: 'Boost Request',
  EMAIL_CONFIRMED: 'Email Confirmed',
  CAMP_CREATED: 'Campaign Created',
  CAMP_DELETED: 'Campaign Deleted',
  CAMP_UPDATED: 'Campaign Updated',
  CLAIM_ORGANISATION_REQUEST: 'Organisation Claim Request',
  HOST_SIGN_UP: 'Host Signup Notification',
  MAGIC_LOGIN: 'Magic Login Request',
  MENTOR_INVITE: 'Mentor Invite',
  NPO_VERIFY_REQUEST: 'Nonprofit Verification Requested',
  OPP_CHECKIN: 'Volunteer Opportunity Checked In',
  OPP_CREATED: 'Opportunity Created',
  OPP_DELETED: 'Opportunity Deleted',
  OPP_DELETED_NOTIF: 'Volunteer Opportunity Deleted Notification',
  OPP_HOST_CHECKIN: 'Host Opportunity Checked In',
  OPP_SIGN_UP: 'Volunteer Opportunity Signup',
  OPP_UPDATED: 'Opportunity Updated',
  OPP_UPDATED_NOTIF: 'Volunteer Opportunity Updated Notification',
  ORG_CREATED: 'Organisation Created',
  OZOW_ACCOUNT_ACTIVATED: 'Ozow Account Activated',
  OZOW_ACCOUNT_REQUESTED: 'Ozow Account Requested',
  PASSWORD_RESET_SUCCESS: 'Password Reset Successful',
  PRINCIPAL_INVITE: 'Principal Invite',
  PRINCIPAL_ONBOARDING_REMINDER: 'Principal Onboarding Reminder',
  ROUND_CONFIRMATION: 'Round Confirmation',
  ROUND_REMINDER: 'Round Reminder',
  ROUND_UPDATE: 'Round Update',
  SUBJECT_REGISTRATION_CONFIRM: 'Subject Registration Confirmation',
  SUPERADMIN_OPPORTUNITY_CREATED: 'Superadmin Cause Created Notification',
  SUPERADMIN_INVITE: 'Superadmin Invitation',
  TRACK_MEMBER_INVITE: 'Member Invitation',
  TRACK_PASSWORD_RESET: 'Password Reset Request',
  OPP_CHECKIN_PROMPT: 'Volunteer Opportunity Checkin Prompt',
  OPP_REMINDER: 'Volunteer Opportunity Reminder Notification',
  HOST_CHECKIN_PROMPT: 'Host Checkin Prompt',
  NPO_VERIFY_ACTIVATED: 'Nonprofit Verification Activated',
  SUBSCRIPTION_UPDATED: 'Subscription Updated',
  WORKSHOP_CONFIRMATION: 'Workshop Confirmation',
  WORKSHOP_REMINDER: 'Workshop Reminder',
  WORKSHOP_UPDATE: 'Workshop Update'
}

const GS_PLATFORM_EVENTS = {
  ACCOUNT_ACTIVATION_CONFIRMATION: 'Account Activation Confirmation',
  COMPETITION_UPDATE: 'Competition Progress Update',
  ENROL_LEARNERS_REMINDER: 'Enrol Learners Reminder',
  MENTOR_INVITE: 'Mentor Invite',
  MENTOR_ENROLMENT_REMINDER: 'Mentor Enrolment Reminder',
  MENTOR_INVITE_CONFIRMATION: 'Mentor Invite Confirmation',
  MENTOR_ONBOARDING_REMINDER: 'Mentor Onboarding Reminder',
  MENTOR_REGISTRATION_REMINDER: 'Mentor Registration Reminder',
  MENTOR_MID_YEAR_UPDATE_LEARNERS_REMINDER: 'Mid-year Results Reminder',
  MENTOR_END_YEAR_UPDATE_LEARNERS_REMINDER: 'End-year Results Reminder',
  PASSWORD_RESET_SUCCESS: 'Password Reset Successful',
  PRINCIPAL_INVITE: 'Principal Invite',
  PRINCIPAL_MENTORS_INVITE_REMINDER: 'Mentor Invite Reminder',
  PRINCIPAL_ONBOARDING_REMINDER: 'Principal Onboarding Reminder',
  ROUND_CONFIRMATION: 'Round Confirmation',
  ROUND_REMINDER: 'Round Reminder',
  ROUND_UPDATE: 'Round Update',
  SUBJECT_REGISTRATION_CONFIRM: 'Subject Registration Confirmation',
  TRACK_PASSWORD_RESET: 'Password Reset Request',
  WORKSHOP_CONFIRMATION: 'Workshop Confirmation',
  WORKSHOP_REMINDER: 'Workshop Reminder',
  WORKSHOP_UPDATE: 'Workshop Update'
}

const PERSONAL_GOAL_VISIBILITY_FORM_OPTS = [
  {
    label: 'Public - visible to everyone',
    value: 'Public',
    iconPadding: '5px',
    Icon: PublicIcon
  },
  {
    label: 'Private - only visible to you.',
    value: 'Private',
    iconPadding: '5px',
    Icon: PrivateIcon
  }
]

const PERSONAL_GOAL_VISIBILITY_OPTS = {
  PUBLIC: 'Public',
  PRIVATE: 'Private',
  ACTION_ONLY: 'ActionOnly'
}

const PERSONAL_GOAL_STATUSES = {
  ACTIVE: 'Active',
  COMPLETE: 'Complete',
  AWAITING_CHECKIN: 'AwaitingCheckin',
  MISSED_CHECKIN: 'MissedCheckin'
}

const PERSONAL_GOAL_FREQUENCIES = {
  ONCE: null,
  DAILY: 'Daily',
  WEEKDAYS: 'Weekdays',
  WEEKENDS: 'Weekends',
  WEEKLY: 'Weekly',
  MONTHLY: 'Monthly',
  QUARTERLY: 'Quarterly',
  ANNUALLY: 'Annually'
}

const STORY_STATES = {
  ADMIN_HIDDEN: 'AdminHidden',
  ANON_DONATION: 'AnonDonation',
  PUBLIC: 'Public',
  PRIVATE: 'Private',
  ACTION_ONLY: 'ActionOnly',
  TITLE_ONLY: 'TitleOnly'
}

const STORY_STATE_NAMES = {
  AnonDonation: 'Anon',
  Public: 'Public',
  Private: 'Private',
  ActionOnly: 'Action Only',
  TitleOnly: 'Title Only'
}

const STORY_CONTEXT_TYPES = {
  APP: 'App',
  CAMP: 'Campaign',
  OPP: 'Opportunity',
  ORG: 'Organisation',
  USER: 'User',
  DONATION_VIA_APP: 'Application',
  PERSON_TO_ORG_DONATION: 'PersonToOrgDonation',
  ORG_TO_ORG_DONATION: 'OrgToOrgDonation',
  // deprecated
  DONATION_TO_ORG: 'DonationToOrg'
}

const STORY_CONTEXT_TYPE_NAMES = {
  App: 'APP',
  Campaign: 'Campaign',
  Opportunity: 'Cause',
  Organisation: 'Organisation',
  User: 'User',
  Application: 'Donation via Cause',
  PersonToOrgDonation: 'Personal donation',
  OrgToOrgDonation: 'Org Donation',
  // deprecated
  DonationToOrg: 'DonationToOrg'
}

const STORY_TYPES = {
  CAMP_FEATURE: 'CampFeature',
  CAMP_UPDATE: 'CampUpdate',
  CHAMPION_FEATURE: 'ChampionFeature',
  DONATION_TO_ORG: 'DonationToOrg',
  DONATION_TO_ORG_CONFIRMED: 'DonationToOrgConfirmed',
  GOOD_DEED: 'GoodDeed',
  OPP_FEATURE: 'OppFeature',
  OPP_FEEDBACK: 'OppFeedback',
  OPP_HOST_FEEDBACK: 'OppHostFeedback',
  OPP_SIGN_UP: 'OppSignUp',
  OPP_SIGN_UP_PLEDGE: 'OppSignUpPledge',
  OPP_SIGN_UP_GOODS: 'OppSignUpGoods',
  OPP_SIGN_UP_MONEY: 'OppSignUpMoney',
  OPP_SIGN_UP_MONEY_CONFIRMED: 'OppSignUpMoneyConfirmed',
  OPP_SIGN_UP_MONEY_CORPORATE: 'OppSignUpMoneyCorporate',
  OPP_SIGN_UP_MONEY_CORPORATE_CONFIRMED: 'OppSignUpMoneyCorporateConfirmed',
  OPP_SIGN_UP_VOLUNTEER: 'OppSignUpVolunteer',
  OPP_CREATED: 'OppCreated',
  OPP_UPDATE: 'OppUpdate',
  ORG_FEATURE: 'OrgFeature',
  ORG_INVITE: 'OrgInvite',
  ORG_UPDATE: 'OrgUpdate'
}

const STORY_TYPE_NAMES = {
  CampFeature: 'Campaign Featured Story',
  CampUpdate: 'Campaign Update',
  ChampionFeature: 'Chamption Featured Story',
  DonationToOrg: 'Donation to Org',
  DonationToOrgConfirmed: 'Donation to Org',
  GoodDeed: 'Good Deed',
  OppFeature: 'Cause Featured Story',
  OppFeedback: 'Cause Feedback',
  OppHostFeedback: 'Cause Host Feedback',
  OppSignUp: 'Cause Signup',
  OppSignUpGoods: 'Cause Signup',
  OppSignUpMoney: 'Cause Signup',
  OppSignUpMoneyCorporate: 'Corporate Donation',
  OppSignUpPledge: 'Cause Signup',
  OppSignUpVolunteer: 'Cause Signup',
  OppSignUpMoneyConfirmed: 'Cause Signup',
  OppSignUpMoneyCorporateConfirmed: 'Corporate Donation',
  OppCreated: 'Cause Created',
  OppUpdate: 'Cause Update',
  OrgFeature: 'Organisation Featured Story',
  OrgInvite: 'Organisation Invitation Story',
  OrgUpdate: 'Organisation Update Story'
}

export default {
  ADDITIONAL_QUESTION_CONTEXTS: { SIGN_UP: 0, CHECK_IN: 1 },
  ADDITIONAL_QUESTION_TYPES: {
    document: 0,
    string_question: 1,
    integer_question: 2,
    // new values

    Text: 11,
    Integer: 12,
    // MultipleChoice: 13,
    Boolean: 14,
    Location: 15,
    Document: 16,
    SingleChoice: 17
  },
  ADDITIONAL_QUESTION_TYPES_INVERSE: {
    0: 'document',
    1: 'question',
    2: 'number question',
    // new values
    11: 'question',
    12: 'number question',
    // 13: 'multiple choice question',
    14: 'true/false question',
    15: 'location',
    16: 'document'
  },
  APPLICATION_STATUSES: {
    Applied: 'applied',
    Notified: 'notified',
    CheckinSent: 'check-in sent',
    ProvidedFeedback: 'feedback provided',
    DidNotAttend: 'did not attend',
    Confirmed: 'confirmed',
    Rejected: 'rejected',
    Cancelled: 'cancelled',
    // Growsmart
    GsEliminated: 'Did not progress',
    GsWinner: 'Finalist',
    GsPendingRound: 'Active',
    GS_PROMOTED: 'Active',
    GS_ABSENT: 'GsAbsent',
    GS_CANCELLED: 'Withdrew'
  },
  APPLICATION_STATUS_NAMES: {
    APPLIED: 'Applied',
    NOTIFIED: 'Notified',
    CHECKIN_SENT: 'CheckinSent',
    PROVIDED_FEEDBACK: 'ProvidedFeedback',
    MARKED_AS_NON_ATTENDED: 'DidNotAttend',
    CONFIRMED: 'Confirmed',
    REJECTED: 'Rejected',
    CANCELLED: 'Cancelled',
    // Growsmart
    GS_ELIMINATED: 'GsEliminated',
    GS_WINNER: 'GsWinner',
    GS_FINALIST: 'GsWinner',
    GS_AWAITING_ROUND: 'GsPendingRound',
    GS_PROMOTED: 'GsPromoted',
    GS_ABSENT: 'GsAbsent',
    GS_CANCELLED: 'GsCancelled'
  },
  BBBEE_LEVELS,
  BENEFACTOR_TYPES,
  BENEFICIARY_TYPES,
  CAMPAIGN_STATUSES: {
    INACTIVE: 'Inactive',
    ACTIVE: 'Active',
    COMPLETED: 'Completed'
  },
  CAMPAIGN_STATUS_NAMES: {
    Applied: 'Applied',
    GsEliminated: 'Did not progress',
    GsWinner: 'Competition finalist',
    GsPendingRound: 'Active',
    GsPromoted: 'Active',
    GsAbsent: 'Absent from competition',
    GsCancelled: 'Withdrew from competition'
  },
  CAMPAIGN_URL:
    process.env.REDUX_ENV === 'production'
      ? `/${l('ROUTE_CAMPS')}/4?title=act-for-change`
      : `/${l('ROUTE_CAMPS')}/1?title=act-for-change`,
  CURRENT_YEAR: 2021,
  CUSTOM_FIELD_NAME_CONTEXTS: {
    ORG: 'Organisation',
    OPP: 'Opportunity',
    CAMP: 'Campaign',
    USER: 'User'
  },
  CUSTOM_FIELD_INPUT: {
    STRING: 'String',
    INT_DROPDOWN: 'IntDropdown',
    BOOL_DROPDOWN: 'BoolDropdown',
    INTEGER: 'Integer',
    ADDRESS: 'Address',
    PHONE: 'Phone',
    EMAIL: 'Email',
    URL: 'Url',
    STRING_DROPDOWN: 'StringDropdown'
  },
  CREATOR_CONTEXTS,
  DONATION_LINK_TYPES: { OZOW: 1, EXTERNAL: 2 },
  DONO_PLATFORM_CONTEXT_TYPES: { ORG: 'Organisation', OPP: 'Opportunity', APP: 'Application' },
  FORM_GOAL_TYPES: goalTypesForForm,
  FORM_GOAL_TYPES_UNBEN: goalTypesForFormUnben,
  GS_LEARNER_GENDERS,
  GS_LEARNER_GRADES,
  GS_LEARNER_RACES,
  GS_RSVP_STATUS_NAMES,
  HOST_CHECKIN_STATUSES: {
    CHECKIN_NOT_YET_SENT: 'CheckinNotSent',
    CHECKIN_SENT: 'CheckinSent',
    PROVIDED_FEEDBACK: 'ProvidedFeedback'
  },
  JWT_REFRESH_THRESHOLD: 60 * 20,
  MAIL_DELIVERY_STATUSES,
  MAX_FILE_SIZE: 15728640, // 15mb
  // MAX_FILE_SIZE: 2097152, // 2mb
  NPO_TYPES: [
    { value: 'npo', label: 'Non-Profit Company' },
    { value: 'trust', label: 'Trust' },
    { value: 'vol_assc', label: 'Voluntary Association' }
  ],
  NPO_VERIFICATION_STATUSES: {
    REQUESTED: 'Requested',
    UNVERIFIED: 'Unverified',
    VERIFIED: 'Verified'
  },
  ONBOARDING_CHOICES: { COMPLETE_SIGNUP: 1, CREATE_ORG: 2 },
  OPP_PERSONAL_CTX_OPT: {
    label: 'In a personal Capacity',
    value: 'personal',
    image: getLocalStorageValues().avatar_url || UserIcon
  },
  OPPORTUNITY_GOAL_TYPE_CTA: goalTypeCtas,
  OPPORTUNITY_GOAL_TYPE_DELIVERABLES: goalTypeDeliverables,
  OPPORTUNITY_GOAL_TYPES: goalTypes,
  OPPORTUNITY_GOAL_TYPE_NAMES: goalTypeNames,
  OPPORTUNITY_STATUSES: {
    INACTIVE: 'Inactive',
    ACTIVE: 'Active',
    CANCELLED: 'Cancelled',
    COMPLETED: 'Completed',
    DRAFT: 'Draft',
    // GS
    GS_AWAITING_ROUND: 'GsPendingRound',
    GS_PROMOTED: 'GsPromoted'
  },
  OPPORTUNITY_STATUS_NAMES: {
    Applied: 'Attending',
    Notified: 'Attending',
    Cancelled: 'Cancelled',
    GsEliminated: 'Did not progress',
    GsWinner: 'Competition finalist',
    GsPendingRound: 'Active',
    GsPromoted: 'Active',
    GsAbsent: 'Did not progress',
    GsCancelled: 'Withdrew from event'
  },
  ORG_STATUSES: {
    ACTIVE: 'Active',
    INACTIVE: 'Inactive'
  },
  OZOW_ACCOUNT_STATES: {
    PENDING: 'Pending',
    ACTIVE: 'Active',
    SUSPENDED: 'Suspended'
  },
  PENDING_IMAGES: {
    UPLOAD_PENDING: 'https://cdn.browniepoints.africa/cause-defaults/pending_upload.png',
    OPP_FALLBACK: 'https://cdn.browniepoints.africa/cause-defaults/cause_fallback.png'
  },
  PERSONAL_GOAL_VISIBILITY_FORM_OPTS,
  PERSONAL_GOAL_VISIBILITY_OPTS,
  PERSONAL_GOAL_STATUSES,
  PERSONAL_GOAL_FREQUENCIES,
  REVERSE_ROLES: {
    GodAdmin: 'God admin',
    SuperAdmin: 'Superadmin',
    Admin: 'Admin',
    Member: 'Member',
    DefaultRole: 'Default role',
    OrgInviter: 'Organisation Inviter',
    // Growsmart roles
    GsPlatformUser: 'Growsmart platform user',
    GsMentor: 'Mentor'
  },
  ROLES: {
    GOD_MODE: 'GodAdmin',
    SUPERADMIN: 'SuperAdmin',
    ADMIN: 'Admin',
    MEMBER: 'Member',
    DEFAULT: 'DefaultRole', // If there are no user roles
    ORG_INVITER: 'OrgInviter',
    // Growsmart roles
    GS_PLATFORM_USER: 'GsPlatformUser',
    MENTOR: 'GsMentor'
  },
  ROLEABLES: {
    ORG: 'Organisation',
    OPP: 'Opportunity',
    CAMP: 'Campaign',
    CATEGORY: 'Category'
  },
  S3_ASSET_PATH: 'https://cdn.browniepoints.africa/assets/',
  STORY_FEED_DEFAULT_LENGTH: 50,
  STORY_STATES,
  STORY_STATE_NAMES,
  STORY_TYPES,
  STORY_TYPE_NAMES,
  STORY_CONTEXT_TYPES,
  STORY_CONTEXT_TYPE_NAMES,
  SUBSCRIPTION_COSTS: subscriptionPrices,
  SUPPORTED_IMG_FORMATS: ['image/jpg', 'image/jpeg', 'image/gif', 'image/png'],
  USER_STATUSES: { ACCEPTED: 'accepted' },
  PLATFORM_EVENTS,

  // Growsmart constants
  GS_PLATFORM_EVENTS,
  GS_PROV_OPTS,
  SCHOLARSHIP_APPLICATION_STATUSES,
  GS_PROV_FULL_OPTS,
  DEFAULT_GS_YEAR,
  GS_YEAR_OPTS,
  GS_CURRENT_YEAR_OPTS,
  GROWSMART_SCHOOL_ORG_TYPE: 'School',
  GROWSMART_ROUND_LEVELS: growsmartRoundLevels,
  GROWSMART_SPONSORSHIP_STATUSES: { ACTIVE: 1, INACTIVE: 2 },
  GROWSMART_SCHOLARSHIP_STATUSES: { ACTIVE: 1, INACTIVE: 2, AWARDED: 3, DENIED: 4 },
  GROWSMART_SPONSORSHIP_CATEGORIES: [
    { label: 'Auditors', value: 'Auditors' },
    { label: 'Catering', value: 'Catering' },
    { label: 'Cleaning services', value: 'Cleaning services' },
    { label: 'Furniture', value: 'Furniture' },
    { label: 'Growsmart dictionaries', value: 'Growsmart dictionaries' },
    { label: 'Print', value: 'Print' },
    { label: 'Prizes', value: 'Prizes' },
    { label: 'Story writing book', value: 'Story writing book' },
    { label: 'Story writing workshop', value: 'Story writing workshop' },
    { label: 'Tickets', value: 'Tickets' },
    { label: 'Other', value: 'Other' }
  ]
}
